import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title = '', 
  description = '', 
  keywords = '',
  canonical = '',
  ogTitle = '',
  ogDescription = '',
  ogImage = '',
  ogUrl = '',
  articleData = null,
  schemaData = null
}) => {
  const siteName = "AI BookMoney";
  const defaultImage = "/ai-manuscript-demo.png";
  const baseUrl = process.env.REACT_APP_BACKEND_URL ? 
    process.env.REACT_APP_BACKEND_URL.replace('/api', '') : 
    'https://aibookmoney.com';

  // Ensure we have at least basic data
  const safeTitle = title || 'AI BookMoney';
  const safeDescription = description || 'AI-powered book creation platform';

  // Synchronous meta tag injection for react-snap pre-rendering
  // This ensures meta tags are in the DOM before react-snap captures the HTML
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Only inject synchronously on initial mount (for react-snap)
    const isReactSnap = navigator.userAgent === 'ReactSnap';
    
    if (isReactSnap) {
      // Direct DOM manipulation for react-snap to ensure immediate availability
      document.title = safeTitle ? `${safeTitle} | ${siteName}` : siteName;
      
      const setOrUpdateMeta = (selector, attribute, attributeValue, content) => {
        let meta = document.querySelector(selector);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attribute, attributeValue);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      const setOrUpdateLink = (rel, href) => {
        let link = document.querySelector(`link[rel="${rel}"]`);
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', rel);
          document.head.appendChild(link);
        }
        link.setAttribute('href', href);
      };

      // Inject basic meta tags
      setOrUpdateMeta('meta[name="description"]', 'name', 'description', safeDescription);
      if (keywords) setOrUpdateMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
      setOrUpdateMeta('meta[name="author"]', 'name', 'author', 'AI BookMoney');
      setOrUpdateMeta('meta[name="robots"]', 'name', 'robots', 'index, follow');

      // Inject Open Graph tags
      setOrUpdateMeta('meta[property="og:title"]', 'property', 'og:title', ogTitle || title);
      setOrUpdateMeta('meta[property="og:description"]', 'property', 'og:description', ogDescription || description);
      setOrUpdateMeta('meta[property="og:image"]', 'property', 'og:image', ogImage ? `${baseUrl}${ogImage}` : `${baseUrl}${defaultImage}`);
      setOrUpdateMeta('meta[property="og:url"]', 'property', 'og:url', ogUrl ? `${baseUrl}${ogUrl}` : `${baseUrl}${canonical || ''}`);
      setOrUpdateMeta('meta[property="og:type"]', 'property', 'og:type', articleData ? 'article' : 'website');
      setOrUpdateMeta('meta[property="og:site_name"]', 'property', 'og:site_name', siteName);

      // Inject Twitter Card tags
      setOrUpdateMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
      setOrUpdateMeta('meta[name="twitter:title"]', 'name', 'twitter:title', ogTitle || title);
      setOrUpdateMeta('meta[name="twitter:description"]', 'name', 'twitter:description', ogDescription || description);
      setOrUpdateMeta('meta[name="twitter:image"]', 'name', 'twitter:image', ogImage ? `${baseUrl}${ogImage}` : `${baseUrl}${defaultImage}`);

      // Canonical URL
      if (canonical) {
        setOrUpdateLink('canonical', `${baseUrl}${canonical}`);
      }

      // JSON-LD Schema
      if (schemaData) {
        let script = document.querySelector('script[type="application/ld+json"][data-react-snap]');
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          script.setAttribute('data-react-snap', 'true');
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(schemaData);
      }

      console.log('✅ [react-snap] Meta tags injected synchronously for:', safeTitle);
    }
  }, [safeTitle, safeDescription, keywords, canonical, ogTitle, ogDescription, ogImage, ogUrl, baseUrl, siteName, defaultImage, articleData, schemaData]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{safeTitle ? `${safeTitle} | ${siteName}` : siteName}</title>
      <meta name="description" content={safeDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="AI BookMoney" />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={`${baseUrl}${canonical}`} />}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={ogImage ? `${baseUrl}${ogImage}` : `${baseUrl}${defaultImage}`} />
      <meta property="og:url" content={ogUrl ? `${baseUrl}${ogUrl}` : `${baseUrl}${canonical || ''}`} />
      <meta property="og:type" content={articleData ? "article" : "website"} />
      <meta property="og:site_name" content={siteName} />
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={ogImage ? `${baseUrl}${ogImage}` : `${baseUrl}${defaultImage}`} />
      
      {/* Article-specific meta tags */}
      {articleData && (
        <>
          <meta property="article:author" content={articleData.author || "AI BookMoney Team"} />
          <meta property="article:published_time" content={articleData.publishedTime} />
          <meta property="article:modified_time" content={articleData.modifiedTime || articleData.publishedTime} />
          <meta property="article:section" content={articleData.section || "Publishing"} />
          {articleData.tags && articleData.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Schema.org JSON-LD */}
      {schemaData && (
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;